import type {
  CascadeResult,
  WorkbenchPred,
  WorkbenchResult,
  WorkbenchRow,
} from "../../../models";

/** Downstream (out-of-section) rows of a publish cascade — the activities IN
 *  the plan are shown in the "What changed" tab, never here. */
export function downstreamRows(cascade: CascadeResult) {
  return cascade.cascaded.filter((c) => c.in_section === false);
}

/** Count of downstream-impacted activities — the red alert badge on the
 *  Cascade tab. */
export function downstreamCount(cascade: CascadeResult): number {
  return downstreamRows(cascade).length;
}

/** activity_id → the activity's LEAF WBS section (the most specific node — the
 *  section it's actually in), for the small line under each name. The full
 *  root→leaf breadcrumb truncates and hides the leaf, which reads as the wrong
 *  section; the leaf is what matters here. */
export function downstreamSectionMap(cascade: CascadeResult): Map<number, string> {
  const m = new Map<number, string>();
  for (const c of downstreamRows(cascade)) {
    if (c.wbs_path && c.wbs_path.length > 0) {
      m.set(c.activity_id, c.wbs_path[c.wbs_path.length - 1]);
    }
  }
  return m;
}

/** Map the publish cascade's DOWNSTREAM ripple into a read-only WorkbenchResult
 *  so the Cascade tab renders in the same WorkbenchRows view used across the
 *  product. one_hop rows get depth=1 so the workbench flags them as the first
 *  ripple ring (directly tied to plan work). No preds/constraints are carried
 *  (the synthetic publish cascade doesn't compute them); this is a read-only
 *  before/after view. */
export function downstreamCascadeToWorkbench(
  cascade: CascadeResult,
  activityWorkCodeMap: Map<number, string>,
): WorkbenchResult {
  // Chronological — earliest new start first, so the gantt + arrows read top
  // to bottom (a driver sits above the row it pushes). ISO dates sort lexically.
  const ordered = [...downstreamRows(cascade)].sort(
    (a, b) => a.new_start.localeCompare(b.new_start) || a.activity_id - b.activity_id,
  );
  const rows: WorkbenchRow[] = ordered.map((c, i) => ({
    activity_id: c.activity_id,
    task_code: c.task_code ?? activityWorkCodeMap.get(c.activity_id) ?? "",
    task_name: c.task_name,
    task_type: c.task_type ?? null,
    status: "",
    old_start: c.old_start,
    new_start: c.new_start,
    old_end: c.old_end,
    new_end: c.new_end,
    days_pushed: c.days_pushed,
    moved: true,
    depth: c.one_hop ? 1 : 2,
    order_index: i,
    is_critical: c.is_critical,
    total_float: c.total_float,
    duration_days: c.duration_days ?? 0,
    milestone: false,
    duration_editable: false,
    // The driver(s) — "what pushed this date". Read-only chips.
    preds: c.pushing_predecessors.map(
      (p): WorkbenchPred => ({
        edge_id: null,
        is_new: false,
        pred_activity_id: p.activity_id,
        pred_task_code: p.task_code ?? "",
        pred_task_name: p.task_name,
        pred_status: "",
        pred_type: p.pred_type,
        lag_hours: p.lag_hours ?? 0,
        driving: true,
        in_section: p.in_section ?? false,
      }),
    ),
    cstr_type: null,
    cstr_date: null,
  }));
  return {
    ok: true,
    error: null,
    logic_results: [],
    actuals_summary: null,
    source: null,
    rows,
    summary: {
      impacted: rows.length,
      cp_count: rows.filter((r) => r.is_critical).length,
      critical_path_impact: cascade.critical_path_impact,
    },
    constraint_violations: [],
    data_date: null,
    source_float: null,
    context_rows: [],
  };
}
